import useAnimation from "@/mono/lib/hooks/useAnimation";
import { useTicker } from "@/mono/lib/hooks/useRaf";
import { lerp, map } from "@/mono/lib/math";
import { EASE_CUSTOM_2 } from "@/mono/lib/styles/easing";
import { useGlobalStore } from "@/mono/store/global";
import SplitText from "mono/components/SplitText";
import { forwardRef, useImperativeHandle, useRef } from "react";
import styled from "styled-components";

const maxY = 110;

// TODO: Make splittext double
const AnimatedHoverText = forwardRef(
  (
    {
      children,
      className = "",
      secondaryTextClassName = "",
      enabled = true,
      ...props
    },
    ref
  ) => {
    const splitText = useRef();
    const boldText = useRef();
    const y = useRef(0);
    const _y = useRef(0);

    const hoverVisible = useRef(false);

    const duration = 0.65;

    const isMobileLayout = useGlobalStore((state) => state.isMobileLayout);
    const isEnabled = useRef(enabled);

    function handleEnter() {
      if (isMobileLayout) return;
      if (!isEnabled.current) return;
      hoverVisible.current = true;
      animateIn();
    }

    function handleLeave() {
      if (isMobileLayout) return;
      if (!isEnabled.current) return;
      hoverVisible.current = false;
      animateOut();
    }

    useImperativeHandle(ref, () => ({
      animateIn: (params) => {
        splitText.current?.animateIn(params);
      },
      animateOut: (params) => {
        splitText.current?.animateOut(params);
      },
      toggleEnabled: (value) => {
        if (value === null || value === "undefined") {
          isEnabled.current = !isEnabled.current;
        } else {
          isEnabled.current = value;
        }
      },
      isHoverVisible: () => hoverVisible.current,
      triggerLeave: handleLeave,
    }));

    /**
     * Hover animation
     */
    const { animateIn, animateOut } = useAnimation({
      inParams: {
        duration,
        ease: EASE_CUSTOM_2,
        onStart: () => {
          start();
        },
        onUpdate: (v) => {
          y.current = v;
        },
        onComplete: () => {},
      },
      outParams: {
        duration,
        ease: EASE_CUSTOM_2,
        onUpdate: (v) => {
          y.current = v;
        },
        onComplete: () => {
          stop();
        },
      },
    });

    const { start, stop } = useTicker(({ delta }) => {
      if (isMobileLayout) return;

      _y.current = lerp(y.current, _y.current, 0.1, delta);
      const splitYLerped = lerp(_y.current, y.current, 0.1, delta);
      const boldYLerped = lerp(_y.current, y.current, 0.02, delta);

      const splitY = map(splitYLerped, 0, 1, 0, -maxY);
      const boldY = map(boldYLerped, 0, 1, maxY, 0);

      splitText.current.element.style.transform = `translateY(${splitY}%)`;
      boldText.current.style.transform = `translateY(${boldY}%)`;
    });

    return (
      <Wrapper
        {...props}
        className={`${className} animated-hover-text`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <SplitText
          shouldRevert={false}
          ref={splitText}
          animateOnScroll={false}
          tag="h2"
        >
          {children}
        </SplitText>
        {!isMobileLayout && (
          <h2 ref={boldText} className={`${secondaryTextClassName} bold-text`}>
            {children}
          </h2>
        )}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  position: relative;
  display: inline-block;

  // HINT: Overflow trick
  clip-path: inset(0 -${maxY}% 0 0);

  .bold-text {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateY(${maxY}%);
    /* opacity: 0; */
    /* pointer-events: none; */
    font-weight: 500 !important;
  }
`;

export default AnimatedHoverText;
